import {inject, Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import {environment} from "@environments/environment";
import {BehaviorSubject, Observable, of, tap, catchError, switchMap, throwError} from "rxjs";
import {Workstats} from "@models/Workstats.model";
import {Result} from "@models/Infofragments.model";
import { webSocket } from "rxjs/webSocket";
import { retry, RetryConfig } from "rxjs/operators";
import {InvioMail} from "@models/InvioMail.model";
import {JwtHelperService} from "@auth0/angular-jwt";
import {AuthenticationService} from "@shared/authentication.service";
import {ToastrService} from "ngx-toastr";


const retryConfig: RetryConfig = {
  delay: 3000,
};

@Injectable({
  providedIn: 'root'
})
export class APIService implements OnDestroy {
  private auth_service = inject(AuthenticationService);
  private toaster = inject(ToastrService);
  private http = inject(HttpClient);


  private _subscriptions: any[] = [];
  public codelists$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public workstats$: BehaviorSubject<Workstats> = new BehaviorSubject<Workstats>({categories: [], mails: {to_send:0, sent: 0, sent_totals:0}});
  public infofragments$: BehaviorSubject<Result> = new BehaviorSubject<Result>({} as Result);
  public customerinfodigests$: BehaviorSubject<InvioMail> = new BehaviorSubject<InvioMail>(null as any);

  public WS!: WebSocket;
  public ws!: any;
  public ws$!: any;

  private JWT_service = new JwtHelperService();

  public currentUser!: any;

  constructor(
  ) {
    this.enableWS();
  }

  ngOnDestroy() {
    if(this.WS) {
      this.WS.close;
    }
    if(this.ws$) {
      this.ws$.complete();
      this.ws$.unsubscribe();
    }
  }

  enableWS() {
    this.ws = webSocket(environment.API.wsURL);
    this.ws$ = this.ws.pipe(
      retry(retryConfig) //support auto reconnect
    ).subscribe((msg: any) => {
      if(msg) {
        this.handleWSMessage(msg);
      }
    });
    this.currentUser = JSON.parse(localStorage.getItem('COMIN_currentUser') || '{}');
    if(this.currentUser && this.currentUser.access) {
      this.ws.next({topic: 'authentication', payload: this.currentUser.access})
    }
  }

  public get(apipath: string, id?: number | null, params?: any): Observable<any> {
    return this.makeHttpRequest('get', apipath, id, params);
  }

  public uploadFile2(apipath: string, formData: FormData) {
    const baseUrl = environment.API.baseURL;
    return this.http.post(baseUrl + apipath, formData, this.jwt({
      reportProgress: true,
      observe: 'events'
    }));
  }

  public uploadFile(apipath: string, formData: FormData): Observable<any> {
    return this.ensureTokenIsNotExpired().pipe(
      switchMap((tokenValid) => {
        if (tokenValid) {
          const baseUrl = environment.API.baseURL;
          const httpOptions = this.jwt({
            reportProgress: true,
            observe: 'events'
          });
          console.log(httpOptions);
          return this.http.post(baseUrl + apipath, formData, httpOptions).pipe(
            catchError((error) => {
              console.error("HTTP request error:", error);
              return throwError(() => error);
            })
          );
        } else {
          console.error("Token is invalid or refresh failed.");
          return of(null);
        }
      })
    );
  }

  public removeFile(apipath:string) {
    return this.makeHttpRequest('delete', apipath, null);
  }

  public post(apipath: string, id: number | null, params: any) {
    return this.makeHttpRequest('post', apipath, id, null, params);
  }

  public put(apipath: string, id: number | null, params: any) {
    return this.makeHttpRequest('put', apipath, id, null, params);
  }

  public patch(apipath: string, id: number | null, params: any) {
    return this.makeHttpRequest('patch', apipath, id, null, params);
  }

  private ensureTokenIsNotExpired(): Observable<boolean> {
    let token = localStorage.getItem("COMIN_currentUser");
    if(token) {
      const T = JSON.parse(token);
      const T_expired = this.JWT_service.isTokenExpired(T['access'], 3600);
      if (T_expired) {
        return this.auth_service.refreshToken(T).pipe(
          catchError(() => of(false)) // Important: Catch refresh errors
        );
      } else {
        return of(true);
      }
    }
    return of(false);
  }

  private jwt(params?: any) {
    this.currentUser = JSON.parse(localStorage.getItem('COMIN_currentUser') || '{}');
    const httpOptions: any = {};

    if (this.currentUser && this.currentUser.access) {
      httpOptions['headers'] =  new HttpHeaders({
        'Authorization': 'Bearer ' + this.currentUser.access
      });
    }

    if (params) {
      params = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '')
      );
      httpOptions['params'] = params;
    }

    return httpOptions;
  }

  private handleWSMessage(res: any) {
    switch (res.topic) {
      case 'customerinfodigests':
        this.customerinfodigests$.next(res.payload);
        break;
      case 'workstats':
        this.workstats$.next(res.payload);
        break;
      case 'infofragments':
        this.infofragments$.next(res.payload);
        break;
    }
  }

  public getWorkStats() {
    this.get('workstats.json').subscribe((workstats: Workstats) => {
      this.workstats$.next(workstats);
    });
  }

  public customerInfodigestUnlockSafe(category_id: any, customer_id: any) {
    this.currentUser = JSON.parse(localStorage.getItem('COMIN_currentUser') || '{}');

    if (this.currentUser && this.currentUser.access) {
      const baseUrl = environment.API.baseURL;

      return fetch(baseUrl+'customerinfodigests/' + category_id + '@' + customer_id+'/unlock', {
        method: 'PUT',
        keepalive: true,
        headers: {'Authorization': 'Bearer ' + this.currentUser.access},
        body: ''
      });
    }
    return of(null); // Return an observable in case currentUser is null or has no access
  }

  public infofragmentUnlockSafe(id:number) {
    this.currentUser = JSON.parse(localStorage.getItem('COMIN_currentUser') || '{}');

    if (this.currentUser && this.currentUser.access) {
      const baseUrl = environment.API.baseURL;

      return fetch(baseUrl+'infofragments/' + id +'/unlock', {
        method: 'PUT',
        keepalive: true,
        headers: {'Authorization': 'Bearer ' + this.currentUser.access},
        body: ''
      });
    }
    return of(null); // Return an observable in case currentUser is null or has no access
  }

  private makeHttpRequest(method: 'get' | 'post' | 'put' | 'patch' | 'delete', apipath: string, id: number | null | undefined, params?: any, body?: any): Observable<any> {
    return this.ensureTokenIsNotExpired().pipe(
      switchMap((tokenValid) => { // Use switchMap here
        if (tokenValid) {
          const baseUrl = environment.API.baseURL;
          const destUrl = id ? apipath + '/' + id : apipath;
          const httpOptions = this.jwt(params); // Get headers *after* token refresh
          let requestObservable: Observable<any>;
          switch (method) {
            case 'get':
              requestObservable = this.http.get<any>(baseUrl + destUrl, httpOptions);
              break;
            case 'post':
              requestObservable = this.http.post<any>(baseUrl + destUrl, body, httpOptions);
              break;
            case 'put':
              requestObservable = this.http.put<any>(baseUrl + destUrl, body, httpOptions);
              break;
            case 'delete':
              requestObservable = this.http.delete<any>(baseUrl + destUrl, httpOptions);
              break;
            default:
              return of(null);
          }
          return requestObservable.pipe(
            tap({
              next: (response: any) => {
                if (response && response.codelists) {
                  this.codelists$.next(response.codelists);
                }
              }
            }),
            catchError((error) => {
              console.error("HTTP request error:", error);
              if(error && error.error && error.error.detail) {
                this.toaster.error(error.error.detail);
              }
              // Re-throw the error, so it can be handled by the subscriber
              return throwError(() => error);
            })
          );
        } else {
          console.error("Token is invalid or refresh failed.");
          return of(null);
        }
      })
    );
  }
}
